/**
 * ClientDeviceConsent - Device data collection & terms consent
 * Collects device/browser info after T&C acceptance
 * Shows what data is collected and why, compliant with Australian Privacy Act
 */

import React, { useState, useEffect } from 'react';
import ClientShell from './ClientShell';
import { clientFetch, navigate } from './api';
import { TERMS_SECTIONS } from '../legal/legalContent';

function getDeviceInfo() {
  const ua = navigator.userAgent;
  const language = navigator.language || 'en';
  const screenRes = `${window.screen.width}x${window.screen.height}`;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const connection = navigator.connection ? {
    type: navigator.connection.effectiveType,
    downlink: navigator.connection.downlink,
    rtt: navigator.connection.rtt,
  } : null;

  let browser = 'Unknown';
  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edg')) browser = 'Edge';

  let os = 'Unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return {
    browser, os, language, screenRes, timezone, connection,
    userAgent: ua,
    timestamp: new Date().toISOString(),
  };
}

const CONSENT_ITEMS = [
  {
    id: 'device_info',
    label: 'Device & Browser Information',
    description: 'Browser type, operating system, screen resolution, language and timezone. Used to troubleshoot portal issues.',
    required: false,
  },
  {
    id: 'usage_analytics',
    label: 'Usage Analytics',
    description: 'Pages visited, features used, session duration, and interaction patterns within the portal. Used to improve our services and your experience.',
    required: false,
  },
  {
    id: 'network_info',
    label: 'Network Information',
    description: 'Connection type, speed estimate, and latency. Used to assess your current internet service quality and recommend improvements.',
    required: false,
  },
  {
    id: 'location_general',
    label: 'General Location',
    description: 'State/region derived from your IP address (not precise GPS). Used to route support to the nearest team and provide region-specific services.',
    required: false,
  },
  {
    id: 'marketing_comms',
    label: 'Marketing Communications',
    description: 'Receive promotional notifications, product updates, and special offers from Talk Australia within the portal and via email.',
    required: false,
  },
  {
    id: 'third_party_analytics',
    label: 'Third-Party Analytics',
    description: 'Anonymous usage data shared with our analytics providers to help us understand aggregate usage patterns. No personal information is shared.',
    required: false,
  },
];

export default function ClientDeviceConsent() {
  const [consents, setConsents] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [error, setError] = useState(null);
  const [existingConsent, setExistingConsent] = useState(null);

  useEffect(() => {
    // Check if consent already given
    const saved = localStorage.getItem('ta_consent_given');
    if (saved) {
      try { setExistingConsent(JSON.parse(saved)); } catch {}
    }

    // Optional items start switched off; the client opts in
    const defaults = {};
    CONSENT_ITEMS.forEach(item => {
      defaults[item.id] = item.required;
    });
    if (saved) {
      try { Object.assign(defaults, JSON.parse(saved).consents || {}); } catch {}
    }
    setConsents(defaults);
  }, []);

  const handleToggle = (id, required) => {
    if (required) return; // Can't uncheck required
    setConsents(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSubmit = async () => {
    if (!termsAccepted || !privacyAccepted) return;
    setSubmitting(true);
    setError(null);

    const deviceInfo = getDeviceInfo();
    const consentData = {
      consents,
      deviceInfo: consents.device_info ? deviceInfo : { timestamp: deviceInfo.timestamp },
      networkInfo: consents.network_info ? deviceInfo.connection : null,
      termsAccepted: true,
      privacyAccepted: true,
      acceptedAt: new Date().toISOString(),
    };

    try {
      await clientFetch('/client/portal/consent', {
        method: 'POST',
        body: JSON.stringify(consentData),
      });

      localStorage.setItem('ta_consent_given', JSON.stringify({
        given: true,
        at: consentData.acceptedAt,
        consents,
      }));

      setSubmitted(true);
    } catch (err) {
      // Don't report consent as recorded unless the server stored it
      setError('We could not save your preferences. Please try again, or contact support@talkaustralia.com.au.');
    }

    setSubmitting(false);
  };

  const s = {
    header: { marginBottom: 32 },
    title: { fontSize: 22, fontWeight: 700, marginBottom: 8 },
    subtitle: { fontSize: 14, color: '#8B92A5', lineHeight: 1.6 },
    card: { backgroundColor: '#1A2332', borderRadius: 12, border: '1px solid #2A3444', padding: 24, marginBottom: 20 },
    sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 16 },
    consentItem: { display: 'flex', gap: 14, padding: '14px 0', borderBottom: '1px solid #2A344433' },
    toggle: (on) => ({
      width: 44, height: 24, borderRadius: 12, cursor: 'pointer', flexShrink: 0, marginTop: 2,
      backgroundColor: on ? '#0057FF' : '#2A3444', position: 'relative', transition: 'background 0.2s',
    }),
    toggleDot: (on) => ({
      width: 18, height: 18, borderRadius: '50%', backgroundColor: '#FFF',
      position: 'absolute', top: 3, left: on ? 23 : 3, transition: 'left 0.2s',
    }),
    label: { fontSize: 14, fontWeight: 600, color: '#FFF' },
    desc: { fontSize: 12, color: '#5A6175', marginTop: 4, lineHeight: 1.5 },
    required: { fontSize: 10, padding: '2px 6px', borderRadius: 3, backgroundColor: 'rgba(0,87,255,0.15)', color: '#5B9DFF', fontWeight: 700, marginLeft: 8 },
    checkbox: (checked) => ({
      width: 20, height: 20, borderRadius: 4, border: `2px solid ${checked ? '#0057FF' : '#2A3444'}`,
      backgroundColor: checked ? '#0057FF' : 'transparent', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, color: '#FFF', flexShrink: 0,
    }),
    submitBtn: {
      padding: '14px 32px', borderRadius: 8, border: 'none',
      background: 'linear-gradient(135deg, #0057FF, #0040CC)', color: '#FFF',
      fontSize: 15, fontWeight: 600, cursor: 'pointer', width: '100%', marginTop: 16,
    },
    disabledBtn: { opacity: 0.5, cursor: 'not-allowed' },
    termsLink: { color: '#3B82F6', textDecoration: 'underline', cursor: 'pointer' },
    success: {
      backgroundColor: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
      borderRadius: 12, padding: 32, textAlign: 'center',
    },
    termsModal: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 300,
    },
    termsContent: {
      backgroundColor: '#0F1729', borderRadius: 16, border: '1px solid #2A3444',
      padding: 32, maxWidth: 600, maxHeight: '80vh', overflowY: 'auto', width: '90%',
    },
  };

  if (submitted || existingConsent?.given) {
    return (
      <ClientShell title="Privacy & Consent">
        <div style={s.success}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: '#10B981', marginBottom: 8 }}>Consent Recorded</div>
          <div style={{ fontSize: 14, color: '#8B92A5', marginBottom: 24 }}>
            Your preferences have been saved. You can update them at any time from this page.
          </div>
          <button
            style={{ padding: '10px 24px', borderRadius: 8, border: '1px solid #2A3444', backgroundColor: 'transparent', color: '#5B9DFF', cursor: 'pointer', fontSize: 13 }}
            onClick={() => { setExistingConsent(null); setSubmitted(false); }}
          >
            Update Preferences
          </button>
        </div>
      </ClientShell>
    );
  }

  const canSubmit = termsAccepted && privacyAccepted;

  return (
    <ClientShell title="Privacy & Consent">
      <div style={s.header}>
        <div style={s.title}>Data Collection & Privacy Consent</div>
        <div style={s.subtitle}>
          Talk Australia takes your privacy seriously. Before we can provide you with the best possible service,
          we ask which optional data you are happy for us to collect. Each item below is off unless you switch it on,
          and the portal works either way. You can change your preferences at any time.
        </div>
      </div>

      {/* Data Collection Consents */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Data Collection Preferences</div>
        {CONSENT_ITEMS.map((item, i) => (
          <div key={item.id} style={{ ...s.consentItem, borderBottom: i === CONSENT_ITEMS.length - 1 ? 'none' : s.consentItem.borderBottom }}>
            <div style={s.toggle(consents[item.id])} onClick={() => handleToggle(item.id, item.required)}>
              <div style={s.toggleDot(consents[item.id])} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={s.label}>{item.label}</span>
                {item.required && <span style={s.required}>REQUIRED</span>}
              </div>
              <div style={s.desc}>{item.description}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Terms & Privacy */}
      <div style={s.card}>
        <div style={s.sectionTitle}>Legal Agreements</div>

        <div style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: '1px solid #2A344433', alignItems: 'flex-start' }}>
          <div style={s.checkbox(termsAccepted)} onClick={() => setTermsAccepted(!termsAccepted)}>
            {termsAccepted ? '✓' : ''}
          </div>
          <div>
            <div style={s.label}>
              I accept the <span style={s.termsLink} onClick={(e) => { e.stopPropagation(); setShowTerms(true); }}>Terms of Service</span>
            </div>
            <div style={s.desc}>
              By accepting, you agree to Talk Australia's terms of service including service level agreements,
              acceptable use policies, and dispute resolution procedures.
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, padding: '12px 0', alignItems: 'flex-start' }}>
          <div style={s.checkbox(privacyAccepted)} onClick={() => setPrivacyAccepted(!privacyAccepted)}>
            {privacyAccepted ? '✓' : ''}
          </div>
          <div>
            <div style={s.label}>
              I accept the <a href="/privacy" target="_blank" rel="noopener noreferrer" style={s.termsLink}>Privacy Policy</a>
            </div>
            <div style={s.desc}>
              In accordance with the Australian Privacy Act 1988 and the Australian Privacy Principles (APPs),
              we will handle your personal information responsibly. You can ask to access or correct your
              personal information at any time, and ask us to delete information we no longer need.
            </div>
          </div>
        </div>
      </div>

      {/* What We Collect Summary */}
      <div style={{ ...s.card, backgroundColor: '#0F1729' }}>
        <div style={{ fontSize: 13, color: '#5A6175', lineHeight: 1.6 }}>
          <strong style={{ color: '#8B92A5' }}>Your Rights:</strong> Under the Australian Privacy Act, you have the right to: access your personal information, request corrections, withdraw optional consents at any time, ask us to delete information we no longer need, and lodge a complaint with the OAIC (Office of the Australian Information Commissioner). Contact our Privacy Officer at support@talkaustralia.com.au for any concerns.
        </div>
      </div>

      {error && (
        <div style={{ fontSize: 13, color: '#F87171', marginTop: 8 }}>{error}</div>
      )}

      <button
        style={{ ...s.submitBtn, ...(canSubmit ? {} : s.disabledBtn) }}
        onClick={handleSubmit}
        disabled={!canSubmit || submitting}
      >
        {submitting ? 'Saving your preferences...' : 'Accept & Continue'}
      </button>

      {/* Terms Modal */}
      {showTerms && (
        <div style={s.termsModal} onClick={() => setShowTerms(false)}>
          <div style={s.termsContent} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Terms of Service</h2>
            <div style={{ fontSize: 13, color: '#BBBEC7', lineHeight: 1.8 }}>
              {TERMS_SECTIONS.map((section, i) => (
                <React.Fragment key={section.heading}>
                  <p style={i ? { marginTop: 16 } : undefined}><strong>{section.heading}</strong></p>
                  <p>{section.body}</p>
                </React.Fragment>
              ))}
            </div>
            <button
              style={{ marginTop: 20, padding: '10px 24px', borderRadius: 8, border: 'none', background: '#0057FF', color: '#FFF', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              onClick={() => setShowTerms(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </ClientShell>
  );
}
